import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronUp, DollarSign, TrendingUp, 
  Zap, Calendar, Package, Wifi, Users, Wrench,
  Building, FileText, Megaphone, Gauge, Clock
} from 'lucide-react';
import { Input } from './ui/input';
import { Slider } from './ui/slider';

const ROICalculator = ({ initialModel }) => {
  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    capex: true,
    opex: true,
    revenue: true
  });

  // CAPEX (Capital Expenditure) - One-time costs
  const [capex, setCapex] = useState({
    equipment: 180000,      // Routers, switches, antennas, cables
    installation: 30000,    // Labor for setup
    permits: 15000,         // Business permits, licenses
    marketing: 10000,       // Initial marketing/launch
    contingency: 6000       // 10% buffer
  });

  // OPEX (Operating Expenses) - Monthly costs
  const [opex, setOpex] = useState({
    bandwidth: 15000,       // Upstream internet costs
    electricity: 5000,      // Power for equipment
    maintenance: 3000,      // Equipment maintenance
    staff: 8000,            // Staff salary/wages
    marketing: 2000,        // Ongoing marketing
    miscellaneous: 2000     // Other expenses
  });

  // Revenue inputs
  const [revenue, setRevenue] = useState({
    subscribers: 80,        // Number of active subscribers
    arpu: 799,              // Average Revenue Per User
    otherIncome: 0          // Installation fees, etc.
  });

  // Calculated totals
  const [totals, setTotals] = useState({
    totalCapex: 0,
    totalOpex: 0,
    totalRevenue: 0,
    monthlyProfit: 0,
    yearlyProfit: 0,
    paybackMonths: 0,
    roi: 0
  });

  // Pre-fill based on model
  useEffect(() => {
    if (initialModel) {
      const modelPresets = {
        1: { // Micro POP - Barangay Starter (₱241,000)
          capex: { equipment: 180000, installation: 30000, permits: 15000, marketing: 10000, contingency: 6000 },
          opex: { bandwidth: 12000, electricity: 3000, maintenance: 2000, staff: 0, marketing: 1000, miscellaneous: 2000 },
          revenue: { subscribers: 60, arpu: 799, otherIncome: 0 }
        },
        2: { // Micro POP - Town Center Hub (₱402,000)
          capex: { equipment: 300000, installation: 50000, permits: 20000, marketing: 20000, contingency: 12000 },
          opex: { bandwidth: 25000, electricity: 8000, maintenance: 5000, staff: 15000, marketing: 3000, miscellaneous: 4000 },
          revenue: { subscribers: 150, arpu: 899, otherIncome: 5000 }
        },
        3: { // Regional Core (₱2,000,000+)
          capex: { equipment: 1500000, installation: 200000, permits: 50000, marketing: 100000, contingency: 150000 },
          opex: { bandwidth: 80000, electricity: 25000, maintenance: 15000, staff: 60000, marketing: 10000, miscellaneous: 10000 },
          revenue: { subscribers: 500, arpu: 999, otherIncome: 20000 }
        }
      };
      
      const preset = modelPresets[initialModel.id];
      if (preset) {
        setCapex(preset.capex);
        setOpex(preset.opex);
        setRevenue(preset.revenue);
      }
    }
  }, [initialModel]);

  // Calculate totals whenever inputs change
  useEffect(() => {
    const totalCapex = Object.values(capex).reduce((a, b) => a + b, 0);
    const totalOpex = Object.values(opex).reduce((a, b) => a + b, 0);
    const totalRevenue = (revenue.subscribers * revenue.arpu) + revenue.otherIncome;
    const monthlyProfit = totalRevenue - totalOpex;
    const yearlyProfit = monthlyProfit * 12;
    const paybackMonths = monthlyProfit > 0 ? Math.ceil(totalCapex / monthlyProfit) : 0;
    const roi = totalCapex > 0 ? ((yearlyProfit / totalCapex) * 100).toFixed(1) : 0;

    setTotals({
      totalCapex,
      totalOpex,
      totalRevenue,
      monthlyProfit,
      yearlyProfit,
      paybackMonths,
      roi
    });
  }, [capex, opex, revenue]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatCurrency = (value) => {
    return '₱' + value.toLocaleString();
  };

  // Input row component with improved slider
  const InputRow = ({ label, value, onChange, icon: Icon, min = 0, max = 1000000, step, showSlider = true }) => {
    // Calculate a reasonable step size based on max value (about 100-200 steps total)
    const calculatedStep = step || Math.max(Math.round(max / 200), 100);
    
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-slate-700/50 last:border-0">
        <div className="flex items-center gap-2 sm:w-48 flex-shrink-0">
          {Icon && <Icon size={16} className="text-cyan-400" />}
          <span className="text-gray-300 text-sm">{label}</span>
        </div>
        <div className="flex-1 flex items-center gap-4">
          {showSlider && (
            <div className="flex-1 hidden sm:block py-2">
              <Slider
                value={[value]}
                onValueChange={(val) => onChange(val[0])}
                min={min}
                max={max}
                step={calculatedStep}
                className="w-full"
              />
            </div>
          )}
          <div className="w-full sm:w-32">
            <Input
              type="number"
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
              className="bg-slate-900 border-slate-600 text-white text-right"
            />
          </div>
        </div>
      </div>
    );
  };

  // Section header component
  const SectionHeader = ({ title, total, expanded, onToggle, colorClass = "text-cyan-400", bgClass = "bg-cyan-500/10" }) => (
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between p-4 ${bgClass} rounded-xl mb-2 transition-all hover:opacity-90`}
    >
      <div className="flex items-center gap-3">
        <span className={`text-lg font-semibold ${colorClass}`}>{title}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className={`text-xl font-bold ${colorClass}`}>{formatCurrency(total)}</span>
        {expanded ? <ChevronUp className={colorClass} /> : <ChevronDown className={colorClass} />}
      </div>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* CAPEX Section */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl overflow-hidden">
        <SectionHeader
          title="CAPEX (Capital Expenditure)"
          total={totals.totalCapex}
          expanded={expandedSections.capex}
          onToggle={() => toggleSection('capex')}
          colorClass="text-purple-400"
          bgClass="bg-purple-500/10"
        />
        {expandedSections.capex && (
          <div className="px-4 pb-4">
            <p className="text-xs text-gray-500 mb-4">One-time startup costs</p>
            <InputRow 
              label="Equipment" 
              value={capex.equipment} 
              onChange={(v) => setCapex({...capex, equipment: v})}
              icon={Package}
              max={2000000}
              step={5000}
            />
            <InputRow 
              label="Installation" 
              value={capex.installation} 
              onChange={(v) => setCapex({...capex, installation: v})}
              icon={Wrench}
              max={500000}
              step={2500}
            />
            <InputRow 
              label="Permits & Licenses" 
              value={capex.permits} 
              onChange={(v) => setCapex({...capex, permits: v})}
              icon={FileText}
              max={100000}
              step={500}
            />
            <InputRow 
              label="Initial Marketing" 
              value={capex.marketing} 
              onChange={(v) => setCapex({...capex, marketing: v})}
              icon={Megaphone}
              max={200000}
              step={1000}
            />
            <InputRow 
              label="Contingency (Buffer)" 
              value={capex.contingency} 
              onChange={(v) => setCapex({...capex, contingency: v})}
              icon={Building}
              max={500000}
              step={2500}
            />
          </div>
        )}
      </div>

      {/* OPEX Section */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl overflow-hidden">
        <SectionHeader
          title="OPEX (Monthly Operating Costs)"
          total={totals.totalOpex}
          expanded={expandedSections.opex}
          onToggle={() => toggleSection('opex')}
          colorClass="text-orange-400"
          bgClass="bg-orange-500/10"
        />
        {expandedSections.opex && (
          <div className="px-4 pb-4">
            <p className="text-xs text-gray-500 mb-4">Recurring monthly expenses</p>
            <InputRow 
              label="Bandwidth/Upstream" 
              value={opex.bandwidth} 
              onChange={(v) => setOpex({...opex, bandwidth: v})}
              icon={Wifi}
              max={200000}
              step={1000}
            />
            <InputRow 
              label="Electricity" 
              value={opex.electricity} 
              onChange={(v) => setOpex({...opex, electricity: v})}
              icon={Zap}
              max={50000}
              step={250}
            />
            <InputRow 
              label="Maintenance" 
              value={opex.maintenance} 
              onChange={(v) => setOpex({...opex, maintenance: v})}
              icon={Wrench}
              max={50000}
              step={250}
            />
            <InputRow 
              label="Staff/Labor" 
              value={opex.staff} 
              onChange={(v) => setOpex({...opex, staff: v})}
              icon={Users}
              max={100000}
              step={500}
            />
            <InputRow 
              label="Marketing" 
              value={opex.marketing} 
              onChange={(v) => setOpex({...opex, marketing: v})}
              icon={Megaphone}
              max={50000}
              step={250}
            />
            <InputRow 
              label="Miscellaneous" 
              value={opex.miscellaneous} 
              onChange={(v) => setOpex({...opex, miscellaneous: v})}
              icon={Building}
              max={50000}
              step={250}
            />
          </div>
        )}
      </div>

      {/* Revenue Section */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl overflow-hidden">
        <SectionHeader
          title="Monthly Revenue"
          total={totals.totalRevenue}
          expanded={expandedSections.revenue}
          onToggle={() => toggleSection('revenue')}
          colorClass="text-green-400"
          bgClass="bg-green-500/10"
        />
        {expandedSections.revenue && (
          <div className="px-4 pb-4">
            <p className="text-xs text-gray-500 mb-4">Expected monthly income</p>
            <InputRow 
              label="Active Subscribers" 
              value={revenue.subscribers} 
              onChange={(v) => setRevenue({...revenue, subscribers: v})}
              icon={Users}
              min={0}
              max={1000}
              step={5}
            />
            <InputRow 
              label="ARPU (Avg. per User)" 
              value={revenue.arpu} 
              onChange={(v) => setRevenue({...revenue, arpu: v})}
              icon={DollarSign}
              min={0}
              max={2000}
              step={10}
            />
            <InputRow 
              label="Other Income" 
              value={revenue.otherIncome} 
              onChange={(v) => setRevenue({...revenue, otherIncome: v})}
              icon={TrendingUp}
              max={100000}
              step={500}
            />
            <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
              <p className="text-xs text-gray-400">
                Subscription Revenue: {revenue.subscribers} × {formatCurrency(revenue.arpu)} = <span className="text-green-400 font-semibold">{formatCurrency(revenue.subscribers * revenue.arpu)}</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Net Profit Summary */}
      <div className={`bg-slate-800/50 backdrop-blur-sm border rounded-2xl p-6 ${
        totals.monthlyProfit >= 0 ? 'border-cyan-500/50' : 'border-red-500/50'
      }`}>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Gauge className="text-cyan-400" />
          Net Profit Analysis
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-900 rounded-xl p-4 text-center">
            <DollarSign className="text-cyan-400 mx-auto mb-2" size={28} />
            <div className={`text-2xl font-bold mb-1 ${totals.monthlyProfit >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
              {formatCurrency(totals.monthlyProfit)}
            </div>
            <div className="text-xs text-gray-400">Monthly Profit</div>
          </div>
          
          <div className="bg-slate-900 rounded-xl p-4 text-center">
            <Calendar className="text-cyan-400 mx-auto mb-2" size={28} />
            <div className="text-2xl font-bold text-cyan-400 mb-1">
              {totals.paybackMonths > 0 ? `${totals.paybackMonths} mo` : '—'}
            </div>
            <div className="text-xs text-gray-400">Payback Period</div>
          </div>
          
          <div className="bg-slate-900 rounded-xl p-4 text-center">
            <TrendingUp className="text-cyan-400 mx-auto mb-2" size={28} />
            <div className={`text-2xl font-bold mb-1 ${totals.yearlyProfit >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
              {formatCurrency(totals.yearlyProfit)}
            </div>
            <div className="text-xs text-gray-400">Yearly Profit</div>
          </div>
          
          <div className="bg-slate-900 rounded-xl p-4 text-center">
            <Zap className="text-cyan-400 mx-auto mb-2" size={28} />
            <div className={`text-2xl font-bold mb-1 ${Number(totals.roi) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
              {totals.roi}%
            </div>
            <div className="text-xs text-gray-400">Annual ROI</div>
          </div>
        </div>

        {/* Profit Breakdown Bar */}
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Revenue vs Expenses</span>
            <span className="text-gray-400">
              {formatCurrency(totals.totalRevenue)} - {formatCurrency(totals.totalOpex)} = {formatCurrency(totals.monthlyProfit)}
            </span>
          </div>
          <div className="h-4 bg-slate-700 rounded-full overflow-hidden flex">
            {totals.totalRevenue > 0 && (
              <>
                <div 
                  className="bg-orange-500 transition-all duration-500"
                  style={{ width: `${Math.min((totals.totalOpex / totals.totalRevenue) * 100, 100)}%` }}
                />
                <div 
                  className={`transition-all duration-500 ${totals.monthlyProfit >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.max(100 - (totals.totalOpex / totals.totalRevenue) * 100, 0)}%` }}
                />
              </>
            )}
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-orange-400">OPEX ({totals.totalRevenue > 0 ? ((totals.totalOpex / totals.totalRevenue) * 100).toFixed(0) : 0}%)</span>
            <span className={totals.monthlyProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
              Profit ({totals.totalRevenue > 0 ? ((totals.monthlyProfit / totals.totalRevenue) * 100).toFixed(0) : 0}%)
            </span>
          </div>
        </div>

        {/* CAPEX Recovery Timeline */}
        <div className="mt-6 p-4 bg-slate-900/50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="text-purple-400" size={16} />
            <span className="text-sm font-medium text-white">Investment Recovery</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 transition-all duration-500"
                  style={{ 
                    width: totals.paybackMonths > 0 
                      ? `${Math.min((12 / totals.paybackMonths) * 100, 100)}%` 
                      : '0%' 
                  }}
                />
              </div>
            </div>
            <span className="text-sm text-gray-400 w-32 text-right">
              {totals.paybackMonths > 0 ? (
                totals.paybackMonths <= 12 
                  ? `${totals.paybackMonths} months` 
                  : `${(totals.paybackMonths / 12).toFixed(1)} years`
              ) : '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Initial investment of {formatCurrency(totals.totalCapex)} will be recovered in approximately {totals.paybackMonths} months
          </p>
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;
